import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Combobox, type ComboboxOption } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import type { GoogleAdsAccountOption, GoogleAdsSelectResult } from '../../lib/googleAdsConnect';
import { listGoogleAdsAccounts, selectGoogleAdsAccount } from './api';

// kyck's own searchable Google Ads account picker — enumerate → pick → bind.
// Reused by the OAuth callback page (first connect) AND the account's
// API-Connections "Change" dialog (re-pick / correct a wrong binding).
//
// The confirmation chip shows name + id + currency so an AU-vs-NZ mismatch (the
// bug this fixes) is obvious BEFORE binding. The list rides Zernio's shared quota,
// so a stale-cache / temporarily-unavailable state is surfaced, never a silent
// empty picker.

function optionFor(a: GoogleAdsAccountOption, boundId: string | null): ComboboxOption {
  const parts = [a.id, a.currency, a.status].filter(Boolean).join(' · ');
  const isBound = boundId !== null && a.id === boundId;
  return {
    value: a.id,
    label: isBound ? `${a.name} (current)` : a.name,
    description: parts,
    keywords: `${a.id} ${a.currency ?? ''} ${a.managerCustomerId ?? ''}`,
  };
}

export function GoogleAdsAccountPicker({
  accountId,
  onDone,
}: {
  accountId: string;
  onDone?: (result: GoogleAdsSelectResult) => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['googleAdsAccounts', accountId];
  const list = useQuery({
    queryKey,
    queryFn: () => listGoogleAdsAccounts(accountId),
  });

  // A forced refresh busts the server's shared-quota cache (?refresh=1); write the
  // result straight into the query cache (ApiConnectionsSection precedent).
  const refresh = useMutation({
    mutationFn: () => listGoogleAdsAccounts(accountId, true),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
  });

  const [selectedId, setSelectedId] = useState<string>('');
  const bind = useMutation({
    mutationFn: () => selectGoogleAdsAccount({ accountId, customerId: selectedId }),
    onSuccess: (res) => onDone?.(res),
  });

  const data = list.data;
  const accounts = data?.accounts ?? [];
  const boundId = data?.boundCustomerId ?? null;

  const options = useMemo<ComboboxOption[]>(
    () => accounts.map((a) => optionFor(a, boundId)),
    [accounts, boundId],
  );
  const selected = accounts.find((a) => a.id === selectedId) ?? null;

  const doRefresh = () => refresh.mutate();

  if (list.isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
        <Spinner className="h-4 w-4" /> Loading Google Ads accounts…
      </div>
    );
  }

  if (list.isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-rose-600">{(list.error as Error).message || 'Could not load the account list.'}</p>
        <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  // Quota-exhausted with no cache to fall back on — the picker can't render a list.
  if (data && !data.available) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            The Google Ads account list is temporarily unavailable (provider quota). This resets on
            Google's side — retry shortly.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={doRefresh} disabled={refresh.isPending}>
          <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data?.stale && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            Showing a cached list — a live refresh hit Google's shared quota. It may be slightly out
            of date.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Combobox
          value={selectedId}
          onValueChange={setSelectedId}
          options={options}
          placeholder="Search by name, customer id or currency…"
          searchPlaceholder="Search accounts…"
          emptyText="No matching Google Ads accounts."
          className="flex-1"
          aria-label="Google Ads account"
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          title="Refresh the account list"
          onClick={doRefresh}
          disabled={refresh.isPending}
        >
          <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
        </Button>
      </div>

      {selected && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <p className="font-medium text-slate-900">{selected.name}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Customer id <span className="font-mono">{selected.id}</span>
            {selected.currency ? ` · ${selected.currency}` : ''}
            {selected.status ? ` · ${selected.status}` : ''}
          </p>
          {boundId && selected.id === boundId && (
            <p className="mt-1 text-xs text-slate-500">This is the currently bound account.</p>
          )}
        </div>
      )}

      {bind.isError && (
        <p className="text-sm text-rose-600">{(bind.error as Error).message || 'Could not bind the account.'}</p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => bind.mutate()}
          disabled={!selectedId || bind.isPending || (boundId !== null && selectedId === boundId)}
        >
          {bind.isPending ? (
            <>
              <Spinner className="h-4 w-4" /> Binding…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" /> Set as Google Ads account
            </>
          )}
        </Button>
        <span className="text-xs text-slate-400">{data?.total ?? 0} accounts</span>
      </div>
    </div>
  );
}
